import Store from '~/store'
import Router from '~/router'
import AuthAPI from 'api/AuthAPI'
import UsersAPI from 'api/UsersAPI'
import CompanyAPI from 'api/CompanyAPI'
import CountryAPI from 'api/CountryAPI'
import RoutesEnum from 'enums/routes.enum'
import LanguageEnum from 'enums/language.enum'
import CountryBackendEnum from 'enums/country_backend.enum'
import UserRole from 'enums/user_role.enum'

import { isSubdomainActive as isSubdomainActiveFunction } from '~/helpers'
import i18n from '~/i18n'
import '~/env'

export default {
    createSession({ accessToken, refreshToken, loginType, adminAccessToken, adminRefreshToken, adminLoginType }) {
        localStorage.setItem('access_token', accessToken)
        localStorage.setItem('refresh_token', refreshToken)
        localStorage.setItem('login_type', loginType)

        if (adminAccessToken && adminRefreshToken && adminLoginType) {
            localStorage.setItem('admin_access_token', adminAccessToken)
            localStorage.setItem('admin_refresh_token', adminRefreshToken)
            localStorage.setItem('admin_login_type', adminLoginType)
        }
    },

    async loadSession() {
        if (this.isSubdomainActive() && !this.isSubdomainCompanyLoaded()) {
            await this.reloadSubdomainCompany()
        }

        if (!this.isLoggedIn()) {
            if (!this.getCountries().length) {
                await this.reloadCountries()
            }
            if (this.getUserCountry) {
                await this.setUserCountryFromIp()
            }
            return Promise.resolve()
        }

        if (this.getUser()?.id) {
            return Promise.resolve()
        }

        await this.reloadCountries()
        await this.setUserCountryFromIp()

        return this.reloadUser()
    },

    logout() {
        const loginType = localStorage.getItem('login_type')
        const refresh_token = localStorage.getItem('refresh_token')

        AuthAPI.logout({ refresh_token })
            .then(() => {
                localStorage.removeItem('access_token')
                localStorage.removeItem('refresh_token')
                localStorage.removeItem('login_type')
                localStorage.removeItem('admin_access_token')
                localStorage.removeItem('admin_refresh_token')
                localStorage.removeItem('admin_login_type')
                localStorage.removeItem('completeProfileModalShown')
                Store.dispatch('auth/clearSession')
                Router.push({ name: RoutesEnum.AUTH.LOGIN, query: { clearSession: loginType } })
            })
            .catch(() => {
                localStorage.removeItem('access_token')
                localStorage.removeItem('refresh_token')
                localStorage.removeItem('login_type')
                localStorage.removeItem('admin_access_token')
                localStorage.removeItem('admin_refresh_token')
                localStorage.removeItem('admin_login_type')
                localStorage.removeItem('completeProfileModalShown')
                Store.dispatch('auth/clearSession')
                Router.push({ name: RoutesEnum.AUTH.LOGIN, query: { text: 'error', type: 'error' } })
            })
    },

    refreshToken() {
        const refresh_token = localStorage.getItem('refresh_token')
        return AuthAPI.refreshToken({ refresh_token })
            .then((res) => {
                const newAccessToken = res.payload.access_token
                localStorage.setItem('access_token', newAccessToken)
                return newAccessToken
            })
            .catch((err) => {
                localStorage.removeItem('access_token')
                localStorage.removeItem('refresh_token')
                localStorage.removeItem('login_type')
                localStorage.removeItem('completeProfileModalShown')
                Store.dispatch('auth/clearSession')
                Router.push({ name: RoutesEnum.AUTH.LOGIN, query: { text: 'error', type: 'error' } })
                throw err
            })
    },

    getToken() {
        const token = localStorage.getItem('access_token')
        return token ? `Bearer ${token}` : undefined
    },

    isLoggedIn() {
        return Store.getters['auth/getUser'].id || Boolean(this.getToken())
    },

    isUserAdmin() {
        return this.getUser().platformRole === UserRole.ADMIN
    },

    isMemberActivated() {
        const adminAccessToken = localStorage.getItem('admin_access_token')
        const accessToken = localStorage.getItem('access_token')
        return adminAccessToken && adminAccessToken !== accessToken
    },

    getUser() {
        return Store.getters['auth/getUser'] || {}
    },

    getUserCountry() {
        return Store.getters['data/getCountryByGeolocation']
    },

    getUserCountryFromLocalstorage() {
        const countryData = JSON.parse(localStorage.getItem('countryByGeolocation'))
        const currentTime = Date.now()

        if (countryData && currentTime < countryData.expiry) {
            return countryData.value
        }

        return null
    },

    getCountries() {
        return Store.getters['data/getCountries']
    },

    isGeolocationRetryAllowed() {
        const geolocationDeniedAt = localStorage.getItem('geolocation_denied_at')

        if (!geolocationDeniedAt) {
            return true
        }

        const deniedTime = new Date(geolocationDeniedAt)
        const now = new Date()
        const daysPassed = (now - deniedTime) / (1000 * 60 * 60 * 24)

        return daysPassed >= process.env.VUE_APP_GEOLOCATION_WAIT_DAYS
    },

    async setUserCountryFromIp() {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' })

        if (permissionStatus.state === 'denied') {
            localStorage.removeItem('countryByGeolocation')
            localStorage.setItem('geolocation_denied_at', new Date().toISOString())
            Store.dispatch('data/setCountryByGeolocation')

            return
        } else {
            localStorage.removeItem('geolocation_denied_at')
        }

        const userCountryFromLocalstorage = this.getUserCountryFromLocalstorage()
        if (userCountryFromLocalstorage) {
            Store.dispatch('data/setCountryByGeolocation', userCountryFromLocalstorage)
            return
        }

        if (this.isSubdomainActive() || !this.isGeolocationRetryAllowed() || !navigator.geolocation) {
            return
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const latitude = position.coords.latitude
                const longitude = position.coords.longitude

                const resCountry = await UsersAPI.getUserCountryByCordinates(latitude, longitude)

                let currentCountry
                if (resCountry?.address.country_code === CountryBackendEnum.BOSNIA.country_code) {
                    currentCountry = CountryBackendEnum.BOSNIA.id
                    if (!this.getUser().id) {
                        localStorage.setItem('locale', LanguageEnum.BIH)
                        i18n.global.locale = LanguageEnum.BIH
                    }
                } else {
                    currentCountry = CountryBackendEnum.SERBIA.id
                }

                if (userCountryFromLocalstorage && userCountryFromLocalstorage === currentCountry) {
                    return
                }

                const expiry = Date.now() + 86400000 // 24 sata
                localStorage.setItem('countryByGeolocation', JSON.stringify({ value: currentCountry, expiry }))

                const newUserCountryFromLocalstorage = this.getUserCountryFromLocalstorage()
                if (newUserCountryFromLocalstorage !== Store.getters['data/getCountryByGeolocation']) {
                    Store.dispatch('data/setCountryByGeolocation', currentCountry)
                }
            },
            () => {
                localStorage.setItem('geolocation_denied_at', new Date().toISOString())
                localStorage.removeItem('countryByGeolocation')
                Store.dispatch('data/setCountryByGeolocation')
            },
        )
    },

    async reloadCountries() {
        const countries = await CountryAPI.getAll()
        Store.dispatch('data/setCountries', countries)
    },

    reloadUser() {
        return UsersAPI.getCurrent()
            .then((user) => {
                Store.dispatch('auth/setUser', user)
                localStorage.setItem('locale', user.language || LanguageEnum.SR)
                i18n.global.locale = user.language || LanguageEnum.SR
            })
            .catch(() => {
                localStorage.removeItem('access_token')
                localStorage.removeItem('refresh_token')
                localStorage.removeItem('login_type')
                Store.dispatch('auth/clearSession')
                Router.push({ name: RoutesEnum.AUTH.LOGIN, query: { text: 'error', type: 'error' } })
            })
    },

    reloadSubdomainCompany() {
        Store.dispatch('configuration/setIsLoading', true)
        return CompanyAPI.getCompanyByDomain(window.location.host?.split('.')[0])
            .then((response) => {
                Store.dispatch('data/setSubdomainCompany', response)
                Store.dispatch('configuration/setIsLoading', false)
            })
            .catch((error) => {
                if (error.error?.message === 'COMPANY.NOT_FOUND') {
                    window.location.href = `${process.env.VUE_APP_URL}?wrong_subdomain=true`
                }
                Store.dispatch('configuration/setIsLoading', false)
            })
    },

    isUserCompleteProfile() {
        return !!(this.getUser().firstName?.length && this.getUser().lastName?.length && this.getUser().mobile?.length)
    },

    areFirstAndLastNameCompleted() {
        return !!(this.getUser().firstName && this.getUser().lastName)
    },

    isMobileCompleted() {
        return !!this.getUser().mobile
    },

    isSubdomainActive() {
        return isSubdomainActiveFunction()
    },

    isSubdomainCompanyLoaded() {
        return !!Store.getters['data/getSubdomainCompany'].id
    },
}
